import { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  Box,
  Container,
  Divider,
  Input,
  Link,
  PrimaryButton,
  Stack,
  Grid,
  Flex,
  Text, LegacyText,
} from "flicket-ui";
import { NextPage } from "next";
import { useRouter } from "next/router";
import { css } from "styled-components";
import useSWR from "swr";
import { Select, Seo, SlateContainer } from "~components";
import { useShopifyCheckout, useShopifySDK } from "~hooks";
import { handleNotFoundRedirect, showToast } from "~lib";
import {
  ImageSlider,
  MerchandiseLayout,
  ProductCard,
} from "~components/merchandise";
import { getInitialPropsWithShopifySDK } from "~lib/shopify/client";
import { ProductQuery, SelectedOptionInput } from "~lib/shopify/sdk";
import { renderPriceRange } from "~lib/shopify/utils";

const OptionSelector = ({
  option,
  index = 0,
  setSelectedOptions,
  selectedOptions = [],
}: {
  option: { name: string; values: string[] };
  index?: number;
  setSelectedOptions: Dispatch<SetStateAction<SelectedOptionInput[]>>;
  selectedOptions?: SelectedOptionInput[];
}) => {
  const { name, values } = option;
  const selectedOption = selectedOptions?.[index];
  return (
    <Box
      width={100}
      css={`
        .react-select__control {
          min-height: 40px;
          height: 40px;
        }

        .react-select__value-container {
          padding: 6px 8px 6px 16px;
        }

        label {
          font-weight: ${(p) => p.theme.fontWeights.demiBold};
          color: ${(p) => p.theme.colors.N700};
          font-size: ${(p) => p.theme.fontSizes[2]};
        }
      `}
    >
      <Select
        label={name}
        options={values.map((value) => ({ label: value, value }))}
        onChange={({ value }) =>
          setSelectedOptions((options) =>
            options.slice(0, index).concat({ name, value })
          )
        }
        value={
          selectedOption
            ? {
                value: selectedOption.value,
                label: selectedOption.value,
              }
            : null
        }
      />
    </Box>
  );
};

const VariantSelection = ({
  product,
}: {
  product: ProductQuery["productByHandle"];
}) => {
  const { query, push } = useRouter();
  const sdk = useShopifySDK();
  const [quantity, setQuantity] = useState("1");
  const { addItemToCart, updatingCart } = useShopifyCheckout();
  const [variantId, setSelectedVariantId] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptionInput[]>(
    []
  );

  const hasOptions =
    product?.options?.length && product?.options?.[0].values?.length > 1;

  const { data, isValidating } = useSWR(
    selectedOptions.length
      ? [query.handle, JSON.stringify(selectedOptions)]
      : null,
    async (_, selectedOptionsString) =>
      sdk
        .variantBySelectedOptions({
          handle: query?.handle as string,
          selectedOptions: JSON.parse(selectedOptionsString),
        })
        .then((res) => {
          if (!res.productByHandle.variantBySelectedOptions) {
            setSelectedVariantId(null);
          }
          return res;
        })
        .catch((err) => {
          setSelectedVariantId(null);
          throw err;
        })
  );

  useEffect(() => {
    if (query.variantId) {
      const variant = product?.variants?.edges?.find(
        ({ node }) => node.id === query.variantId
      );
      if (variant) {
        setSelectedVariantId(variant.node.id);
        setSelectedOptions(variant.node.selectedOptions);
      }
    }
  }, []);

  useEffect(() => {
    const variantBySelectedOptions =
      data?.productByHandle?.variantBySelectedOptions;
    if (variantBySelectedOptions) {
      setSelectedVariantId(variantBySelectedOptions.id);
      if (query.variantId !== variantBySelectedOptions.id) {
        push(
          {
            pathname: "/merchandise/[handle]",
            query: {
              variantId: variantBySelectedOptions.id,
              handle: query.handle,
            },
          },
          undefined,
          { shallow: true }
        );
      }
    }
  }, [data]);

  useEffect(() => {
    if (!hasOptions) {
      setSelectedVariantId(product?.variants?.edges?.[0]?.node?.id);
    }
  }, [hasOptions]);

  const foundVariant = product.variants?.edges?.find(
    ({ node }) => node.id === variantId
  )?.node;

  return (
    <>
      {hasOptions && (
        <Stack space={2} mt={5} alignItems="center">
          {product.options.map((option, i) => (
            <OptionSelector
              key={i}
              index={i}
              option={option}
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
            />
          ))}
        </Stack>
      )}
      <Divider display={{ _: "none", md: "flex" }} mt={5} />
      <Stack
        space={2}
        mt={{ _: "6/4", md: 3 }}
        alignItems="center"
        justifyContent="flex-start"
      >
        <LegacyText variant="demiBold.M" color="N700">
          Quantity
        </LegacyText>
        <Box
          css={`
            input {
              height: 40px;
              width: 40px;
              padding: 10px 8px;
              text-align: center;
            }
          `}
        >
          <Input
            type="number"
            disabled={!foundVariant}
            min="1"
            required
            value={quantity}
            onChange={(e) =>
              Number(e.currentTarget.value) <= foundVariant?.quantityAvailable
                ? setQuantity(e.currentTarget.value)
                : showToast(
                    `Only ${foundVariant?.quantityAvailable} available`,
                    "error"
                  )
            }
          />
        </Box>
      </Stack>
      <Flex
        flexDir={{ _: "column", md: "row" }}
        mt={{ _: 0, md: 3 }}
        alignItems={{ _: "flex-start", md: "center" }}
      >
        <Divider my={2} display={{ _: "flex", md: "none" }} />
        <Flex>
          <LegacyText
            fontSize={hasOptions ? 7 : 9}
            fontWeight="extraBold"
            color="N600"
          >
            {foundVariant
              ? `$${Number(foundVariant?.priceV2?.amount).toFixed(2)}`
              : renderPriceRange(product.priceRange)}
          </LegacyText>
          {foundVariant && foundVariant.compareAtPriceV2 && (
            <LegacyText
              textDecoration="line-through"
              fontSize={hasOptions ? 7 : 9}
              fontWeight="medium"
              color="N400"
              ml={"1/2"}
            >
              ${Number(foundVariant.compareAtPriceV2.amount).toFixed(2)}
            </LegacyText>
          )}
        </Flex>
        <Divider my={2} display={{ _: "flex", md: "none" }} />
        <PrimaryButton
          ml={{ _: 0, md: 3 }}
          fontSize={4}
          disabled={!variantId || !foundVariant?.availableForSale}
          isLoading={updatingCart || isValidating}
          onClick={() =>
            addItemToCart({ variantId, quantity: parseInt(quantity) })
          }
        >
          Add to cart
        </PrimaryButton>
      </Flex>
    </>
  );
};

const MerchandiseDetailPage: NextPage<{
  product: ProductQuery["productByHandle"];
}> = ({ product }) => {
  const sdk = useShopifySDK();
  const { query } = useRouter();
  const { data } = useSWR(
    ["productRecommendations", product.id],
    async (_, productId) => sdk.productRecommendations({ productId })
  );

  const selectedVariant = product.variants.edges.find(
    ({ node }) => node.id === query.variantId
  )?.node;

  const inStock = selectedVariant
    ? selectedVariant.availableForSale
    : product.availableForSale;

  return (
    <>
      <Seo title={product.title} description={product.description} />
      <MerchandiseLayout type="secondary" title={product.title}>
        <Container mb={10}>
          <Stack space={3} flexDir={["column", "column", "column", "row"]}>
            <Box position="relative" flex={1}>
              <Box
                backgroundColor="P300"
                position="absolute"
                top={0}
                left={0}
                m={3}
                p="1/2"
                lineHeight="normal"
                borderRadius="xs"
              >
                <LegacyText
                  letterSpacing={"-0.165px" as any}
                  fontSize={1}
                  textTransform="uppercase"
                  color="white"
                  fontWeight="demiBold"
                >
                  {inStock ? "In Stock" : "Out of stock"}
                </LegacyText>
              </Box>

              <ImageSlider
                images={product.images.edges.map(
                  ({ node }) => node.originalSrc
                )}
              />
            </Box>
            <Box flex={1}>
              <SlateContainer
                type="merch"
                dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
              />
              <VariantSelection product={product} />
            </Box>
          </Stack>
          {!!data?.productRecommendations?.length && (
            <>
              <Divider mt={3} />
              <LegacyText
                fontWeight="extraBold"
                color="N800"
                lineHeight="normal"
                fontSize={4}
                mt={6}
                mb={2}
              >
                Recommended items
              </LegacyText>
              <Divider mb={4} />
              <Grid rowSize={[1, null, 2, 4]} columnGap={3} rowGap={3} mt={3}>
                {data.productRecommendations.slice(0, 4).map((product) => (
                  <Link
                    key={product.id}
                    to="/merchandise/[handle]"
                    linkAs={`/merchandise/${product.handle}`}
                  >
                    <Box css={css({ "& > *": { height: "100%" } })}>
                      <ProductCard
                        price={renderPriceRange(product.priceRange)}
                        title={product.title}
                        inStock={product.availableForSale}
                        imgSrc={product?.images?.edges?.[0]?.node?.url}
                      />
                    </Box>
                  </Link>
                ))}
              </Grid>
            </>
          )}
        </Container>
      </MerchandiseLayout>
    </>
  );
};

MerchandiseDetailPage.getInitialProps = getInitialPropsWithShopifySDK(
  async (ctx) => {
    const { query, sdk } = ctx;
    const product = await sdk.product({ handle: query.handle as string });
    if (!product.productByHandle) {
      return handleNotFoundRedirect(ctx);
    }
    return {
      product: product.productByHandle,
    };
  }
);

export default MerchandiseDetailPage;
